import { Router } from "express";

import { chatController } from "../controllers/chat-controller";
import { classController } from "../controllers/class-controller";
import { courseController } from "../controllers/course-controller";
import { scheduleChangeController } from "../controllers/schedule-change-controller";
import { schoolController } from "../controllers/school-controller";
import { semesterController } from "../controllers/semester-controller";
import {
  authenticate,
  requireEmailVerification,
} from "../middleware/auth-middleware";
import { createRateLimit } from "../middleware/auth-middleware";
import { requireRole } from "../middleware/role-middleware";
import {
  attachSchoolContext,
  requireSchoolContext,
} from "../middleware/school-context-middleware";
import { uploadCourseSyllabus } from "../middleware/upload-middleware";
import { createCourseValidation } from "../validation/course-validation";
import {
  createClassValidation,
  updateClassValidation,
} from "../validation/school-validation";

const router = Router();

// Apply authentication and school role requirement to all routes
router.use(authenticate);
router.use(requireEmailVerification);
router.use(requireRole(["school"]));
router.use(attachSchoolContext);

/**
 * SCHOOL DASHBOARD ROUTES
 */

/**
 * @route   GET /api/v1/school/dashboard
 * @desc    Get comprehensive school dashboard with pending requests
 * @access  Private (School Admin)
 */
router.get("/dashboard", requireSchoolContext, (req, res) =>
  schoolController.getDashboard(req, res),
);

/**
 * @route   GET /api/v1/school/dashboard/stats
 * @desc    Get quick statistics for school dashboard
 * @access  Private (School Admin)
 */
router.get("/dashboard/stats", requireSchoolContext, (req, res) =>
  schoolController.getQuickStats(req, res),
);

/**
 * COURSE ROUTES (Catalog Entries)
 * Courses must be created before classes, as classes are linked to courses
 */

/**
 * @route   POST /api/v1/school/courses
 * @desc    Create a new course (catalog entry). Supports multipart/form-data with optional syllabusFile.
 * @access  Private (School Admin)
 */
router.post(
  "/courses",
  requireSchoolContext,
  uploadCourseSyllabus,
  createCourseValidation,
  (req, res) => courseController.createCourse(req, res),
);

/**
 * @route   GET /api/v1/school/courses
 * @desc    Get all courses for the school
 * @access  Private (School Admin)
 */
router.get("/courses", requireSchoolContext, (req, res) =>
  courseController.getAllCourses(req, res),
);

/**
 * @route   GET /api/v1/school/courses/statistics
 * @desc    Get course statistics
 * @access  Private (School Admin)
 */
router.get("/courses/statistics", requireSchoolContext, (req, res) =>
  courseController.getCourseStatistics(req, res),
);

/**
 * @route   GET /api/v1/school/courses/:id
 * @desc    Get course by ID
 * @access  Private (School Admin)
 */
router.get("/courses/:id", requireSchoolContext, (req, res) =>
  courseController.getCourseById(req, res),
);

/**
 * @route   PUT /api/v1/school/courses/:id
 * @desc    Update course
 * @access  Private (School Admin)
 */
router.put("/courses/:id", requireSchoolContext, (req, res) =>
  courseController.updateCourse(req, res),
);

/**
 * @route   DELETE /api/v1/school/courses/:id
 * @desc    Delete course
 * @access  Private (School Admin)
 */
router.delete("/courses/:id", requireSchoolContext, (req, res) =>
  courseController.deleteCourse(req, res),
);

/**
 * SEMESTER ROUTES
 */

/**
 * @route   POST /api/v1/school/semesters
 * @desc    Create a new semester
 * @access  Private (School Admin)
 */
router.post("/semesters", requireSchoolContext, (req, res) =>
  semesterController.createSemester(req, res),
);

/**
 * @route   GET /api/v1/school/semesters
 * @desc    Get all semesters for the school
 * @access  Private (School Admin)
 */
router.get("/semesters", requireSchoolContext, (req, res) =>
  semesterController.getAllSemesters(req, res),
);

/**
 * @route   GET /api/v1/school/semesters/current
 * @desc    Get current active semester
 * @access  Private (School Admin)
 */
router.get("/semesters/current", requireSchoolContext, (req, res) =>
  semesterController.getCurrentSemester(req, res),
);

/**
 * @route   GET /api/v1/school/semesters/:id
 * @desc    Get semester by ID
 * @access  Private (School Admin)
 */
router.get("/semesters/:id", requireSchoolContext, (req, res) =>
  semesterController.getSemesterById(req, res),
);

/**
 * @route   PUT /api/v1/school/semesters/:id
 * @desc    Update semester
 * @access  Private (School Admin)
 */
router.put("/semesters/:id", requireSchoolContext, (req, res) =>
  semesterController.updateSemester(req, res),
);

/**
 * @route   DELETE /api/v1/school/semesters/:id
 * @desc    Delete semester
 * @access  Private (School Admin)
 */
router.delete("/semesters/:id", requireSchoolContext, (req, res) =>
  semesterController.deleteSemester(req, res),
);

/**
 * @route   PUT /api/v1/school/semesters/:id/activate
 * @desc    Activate semester
 * @access  Private (School Admin)
 */
router.put("/semesters/:id/activate", requireSchoolContext, (req, res) =>
  semesterController.activateSemester(req, res),
);

/**
 * @route   PUT /api/v1/school/semesters/:id/deactivate
 * @desc    Deactivate semester
 * @access  Private (School Admin)
 */
router.put("/semesters/:id/deactivate", requireSchoolContext, (req, res) =>
  semesterController.deactivateSemester(req, res),
);

/**
 * @route   PUT /api/v1/school/semesters/:id/complete
 * @desc    Complete semester
 * @access  Private (School Admin)
 */
router.put("/semesters/:id/complete", requireSchoolContext, (req, res) =>
  semesterController.completeSemester(req, res),
);

/**
 * @route   GET /api/v1/school/semesters/academic-year/:academicYear
 * @desc    Get semesters by academic year
 * @access  Private (School Admin)
 */
router.get(
  "/semesters/academic-year/:academicYear",
  requireSchoolContext,
  (req, res) => semesterController.getSemestersByAcademicYear(req, res),
);

/**
 * @route   GET /api/v1/school/semesters/statistics
 * @desc    Get semester statistics
 * @access  Private (School Admin)
 */
router.get("/semesters/statistics", requireSchoolContext, (req, res) =>
  semesterController.getSemesterStatistics(req, res),
);

/**
 * CLASS ROUTES
 */

/**
 * @route   POST /api/v1/school/classes
 * @desc    Create a new class
 * @access  Private (School Admin)
 */
router.post(
  "/classes",
  requireSchoolContext,
  createClassValidation,
  (req, res) => classController.createClass(req, res),
);

/**
 * @route   GET /api/v1/school/classes
 * @desc    Get all classes for the school (optional: ?semesterId=xxx)
 * @access  Private (School Admin)
 */
router.get("/classes", requireSchoolContext, (req, res) =>
  classController.getAllClasses(req, res),
);

/**
 * @route   GET /api/v1/school/classes/:id
 * @desc    Get class by ID
 * @access  Private (School Admin)
 */
router.get("/classes/:id", requireSchoolContext, (req, res) =>
  classController.getClassById(req, res),
);

/**
 * @route   PUT /api/v1/school/classes/:id
 * @desc    Update class
 * @access  Private (School Admin)
 */
router.put(
  "/classes/:id",
  requireSchoolContext,
  updateClassValidation,
  (req, res) => classController.updateClass(req, res),
);

/**
 * @route   DELETE /api/v1/school/classes/:id
 * @desc    Delete class
 * @access  Private (School Admin)
 */
router.delete("/classes/:id", requireSchoolContext, (req, res) =>
  classController.deleteClass(req, res),
);

/**
 * TEACHER ASSIGNMENT ROUTES
 */

/**
 * @route   POST /api/v1/school/classes/:id/assign-teacher
 * @desc    Assign teacher to class
 * @access  Private (School Admin)
 */
router.post("/classes/:id/assign-teacher", requireSchoolContext, (req, res) =>
  classController.assignTeacher(req, res),
);

/**
 * @route   GET /api/v1/school/teachers/search
 * @desc    Typeahead search for teachers (async searchable dropdown with pagination)
 * @access  Private (School Admin)
 * @query   search (required, min 2 chars), subject, page, limit
 */
router.get("/teachers/search", requireSchoolContext, (req, res) =>
  classController.searchTeachers(req, res),
);

/**
 * @route   GET /api/v1/school/teachers
 * @desc    Get all school teachers for assignment (id, name, profileImage only)
 * @access  Private (School Admin)
 */
router.get("/teachers", requireSchoolContext, (req, res) =>
  classController.getSchoolTeachers(req, res),
);

/**
 * @route   GET /api/v1/school/teachers/:teacherId
 * @desc    Get specific teacher by ID for school admin
 * @access  Private (School Admin)
 */
router.get("/teachers/:teacherId", requireSchoolContext, (req, res) =>
  classController.getSchoolTeacherById(req, res),
);

/**
 * STUDENT ENROLLMENT ROUTES
 */

/**
 * @route   POST /api/v1/school/classes/:id/enroll-students
 * @desc    Enroll students in class
 * @access  Private (School Admin)
 */
router.post("/classes/:id/enroll-students", requireSchoolContext, (req, res) =>
  classController.enrollStudents(req, res),
);

/**
 * @route   GET /api/v1/school/classes/:id/students
 * @desc    Get all enrolled students for a class
 * @access  Private (School Admin)
 */
router.get("/classes/:id/students", requireSchoolContext, (req, res) =>
  classController.getEnrolledStudents(req, res),
);

/**
 * @route   GET /api/v1/school/students/search
 * @desc    Typeahead search for students (async searchable dropdown with pagination)
 * @access  Private (School Admin)
 * @query   search (required, min 2 chars), academicLevel, academicYear, page, limit
 */
router.get("/students/search", requireSchoolContext, (req, res) =>
  classController.searchStudents(req, res),
);

/**
 * @route   GET /api/v1/school/students
 * @desc    Get all school students for enrollment (id, name, profileImage only)
 * @access  Private (School Admin)
 */
router.get("/students", requireSchoolContext, (req, res) =>
  classController.getSchoolStudents(req, res),
);

/**
 * @route   GET /api/v1/school/students/:studentId
 * @desc    Get specific student by ID for school admin
 * @access  Private (School Admin)
 */
router.get("/students/:studentId", requireSchoolContext, (req, res) =>
  classController.getSchoolStudentById(req, res),
);

/**
 * SEMESTER-CLASS RELATIONSHIP ROUTES
 */

/**
 * @route   GET /api/v1/school/semesters/:semesterId/classes
 * @desc    Get all classes for a specific semester with teacher details
 * @access  Private (School Admin)
 */
router.get("/semesters/:semesterId/classes", requireSchoolContext, (req, res) =>
  classController.getClassesBySemester(req, res),
);

/**
 * SCHOOL CHAT ROUTES
 */

const chatRateLimit = createRateLimit.general();

/**
 * @route   GET /api/v1/school/chats/teachers
 * @desc    Get filtered teachers for school panel chat creation (by name, email)
 * @access  Private (School)
 */
router.get("/chats/teachers", requireSchoolContext, chatRateLimit, (req, res) =>
  chatController.getSchoolTeachersForChat(req, res),
);

/**
 * @route   GET /api/v1/school/chats/students
 * @desc    Get filtered students for school panel chat creation (by name, email)
 * @access  Private (School)
 */
router.get("/chats/students", requireSchoolContext, chatRateLimit, (req, res) =>
  chatController.getSchoolStudentsForChat(req, res),
);

/**
 * @route   POST /api/v1/school/chats
 * @desc    Create chat from school panel with teacher or student
 * @access  Private (School)
 */
router.post("/chats", requireSchoolContext, chatRateLimit, (req, res) =>
  chatController.createSchoolChat(req, res),
);

router.put(
  "/schedule-change-requests/:id/approve",
  requireSchoolContext,
  chatRateLimit,
  (req, res) => scheduleChangeController.approveScheduleChangeRequest(req, res),
);

router.put(
  "/schedule-change-requests/:id/reject",
  requireSchoolContext,
  chatRateLimit,
  (req, res) => scheduleChangeController.rejectScheduleChangeRequest(req, res),
);

export default router;
